import { useRef, useState } from 'react'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'
import type { AudioRecorder } from '../../hooks/useAudioRecorder'
import { toast } from './Toast'
import { useLocale } from '../../i18n/context'

const MAX_UPLOAD_MB = 5
const MAX_RECORD_S = 120
const AUDIO_URL_RE = /^https?:\/\/.+\.(mp3|m4a|ogg|wav|webm|aac|opus|flac)(\?.*)?$/i

function isValidAudioContent(content: string): boolean {
  return content.startsWith('data:audio/') || AUDIO_URL_RE.test(content.trim())
}

interface Props {
  content: string
  onChange: (content: string) => void
}

/**
 * Audio side authoring widget. Handles three states:
 *  - empty: shows Upload + Record actions
 *  - has valid audio (data:audio/ or https audio URL): shows native player + replace actions
 *  - has invalid content (legacy non-audio text): shows error banner + replace prompt
 */
export default function AudioSideInput({ content, onChange }: Props) {
  const { tr } = useLocale()
  const fileRef = useRef<HTMLInputElement>(null)
  const [mode, setMode] = useState<'view' | 'record' | 'url'>('view')
  const [urlDraft, setUrlDraft] = useState('')
  const recorder = useAudioRecorder(MAX_RECORD_S)

  const hasAudio = isValidAudioContent(content)
  const isInvalid = content.length > 0 && !hasAudio

  async function handleFile(file: File) {
    if (!file.type.startsWith('audio/')) {
      toast.error(tr.sideInput.errChooseAudioFile)
      return
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(tr.sideInput.errAudioTooLarge(MAX_UPLOAD_MB))
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => onChange(e.target?.result as string)
    reader.onerror = () => toast.error(tr.sideInput.errCouldNotReadAudio)
    reader.readAsDataURL(file)
  }

  function commitUrl() {
    const url = urlDraft.trim()
    if (!AUDIO_URL_RE.test(url)) {
      toast.error(tr.sideInput.errUrlNotAudio)
      return
    }
    onChange(url)
    setUrlDraft('')
    setMode('view')
  }

  function acceptRecording() {
    if (recorder.dataUrl) {
      onChange(recorder.dataUrl)
      recorder.reset()
      setMode('view')
    }
  }

  function cancelRecording() {
    recorder.reset()
    setMode('view')
  }

  async function openRecorder() {
    setMode('record')
    await recorder.start()
  }

  // ── Recording panel ──────────────────────────────────────────────────────────
  if (mode === 'record') {
    return (
      <div style={{ flex: 1 }}>
        <RecorderPanel
          recorder={recorder}
          maxSeconds={MAX_RECORD_S}
          onAccept={acceptRecording}
          onCancel={cancelRecording}
        />
      </div>
    )
  }

  // ── URL entry panel ──────────────────────────────────────────────────────────
  if (mode === 'url') {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="url"
            value={urlDraft}
            autoFocus
            placeholder="https://example.com/audio.mp3" // i18n-exempt: URL example, not language content
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitUrl() }
              if (e.key === 'Escape') setMode('view')
            }}
            style={{
              flex: 1, padding: '5px 8px', fontSize: 12,
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', outline: 'none',
              fontFamily: 'var(--font-ui)',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          />
          <button type="button" onClick={commitUrl}
            style={{ ...pill, background: 'var(--accent-fill)', color: 'var(--text-on-accent)', borderColor: 'var(--accent-fill)' }}
          >
            {tr.sideInput.add}
          </button>
          <button type="button" onClick={() => setMode('view')} style={pill}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {tr.sideInput.supportedAudioFormats('.mp3, .m4a, .wav, .ogg, .webm')}
        </div>
      </div>
    )
  }

  // ── View / empty / invalid panel ─────────────────────────────────────────────
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        ref={fileRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={async (e) => { const f = e.target.files?.[0]; if (f) await handleFile(f); e.target.value = '' }}
      />

      {isInvalid ? (
        <div style={{
          padding: '10px 12px',
          background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 12, color: 'var(--danger)', lineHeight: 1.5,
        }}>
          {tr.sideInput.invalidAudio}
        </div>
      ) : hasAudio ? (
        <audio
          src={content}
          controls
          preload="metadata"
          style={{ width: '100%', height: 36 }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          style={{
            padding: '12px 0', background: 'var(--bg-secondary)',
            border: '1.5px dashed var(--border)', borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
            textAlign: 'center', transition: 'border-color 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)' }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)' }}
        >
          {tr.sideInput.clickToUploadAudio}
          <div style={{ fontSize: 10, marginTop: 2, opacity: 0.65 }}>MP3, M4A, WAV, OGG, WebM… {/* i18n-exempt: format names */}</div>
        </button>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {recorder.isSupported && (
          <button type="button" onClick={openRecorder}
            style={{ ...pill, background: 'color-mix(in srgb, var(--danger) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--danger) 35%, transparent)', color: 'var(--danger)' }}
          >
            ● {hasAudio || isInvalid ? tr.sideInput.reRecord : tr.sideInput.record}
          </button>
        )}
        <button type="button" onClick={() => fileRef.current?.click()} style={pill}>
          {hasAudio || isInvalid ? tr.sideInput.replaceFile : tr.sideInput.uploadFile}
        </button>
        <button type="button" onClick={() => { setUrlDraft(hasAudio && !content.startsWith('data:') ? content : ''); setMode('url') }} style={pill}>
          {tr.sideInput.fromUrl}
        </button>
        {(hasAudio || isInvalid) && (
          <button type="button" onClick={() => onChange('')}
            style={{ ...pill, color: 'var(--danger)', borderColor: 'color-mix(in srgb, var(--danger) 35%, transparent)' }}
          >
            {tr.sideInput.remove}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Recorder panel ─────────────────────────────────────────────────────────────

function fmtTime(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function RecorderPanel({
  recorder,
  maxSeconds,
  onAccept,
  onCancel,
}: {
  recorder: AudioRecorder
  maxSeconds: number
  onAccept: () => void
  onCancel: () => void
}) {
  const { tr } = useLocale()
  const { status, elapsed, dataUrl, error } = recorder

  if (status === 'requesting') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{tr.sideInput.waitingForMic}</div>
        <button type="button" onClick={onCancel} style={pill}>{tr.sideInput.cancel}</button>
      </div>
    )
  }

  if (status === 'recording') {
    const pct = Math.min((elapsed / maxSeconds) * 100, 100)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
          background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--danger) 25%, transparent)',
          borderRadius: 'var(--radius-sm)',
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)', flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-ui)' }}>
            {fmtTime(elapsed)} / {fmtTime(maxSeconds)}
          </span>
          <div style={{ flex: 1, height: 3, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: 'var(--danger)', transition: 'width 0.9s linear' }} />
          </div>
        </div>
        <button type="button" onClick={recorder.stop}
          style={{ padding: '7px 18px', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)', alignSelf: 'flex-start' }}
        >
          ■ {tr.sideInput.stop}
        </button>
      </div>
    )
  }

  if (status === 'stopped' && dataUrl) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {tr.sideInput.previewRecorded(fmtTime(elapsed))}
        </div>
        <audio src={dataUrl} controls preload="auto" style={{ width: '100%', height: 36 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={onAccept}
            style={{ ...pill, background: 'var(--accent-fill)', color: 'var(--text-on-accent)', borderColor: 'var(--accent-fill)' }}
          >
            {tr.sideInput.useThis}
          </button>
          <button type="button" onClick={() => recorder.reset()} style={pill}>{tr.sideInput.reRecord}</button>
          <button type="button" onClick={onCancel} style={pill}>{tr.sideInput.cancel}</button>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{
          padding: '10px 12px',
          background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--danger) 25%, transparent)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 12, color: 'var(--danger)', lineHeight: 1.5,
        }}>
          {error ?? tr.sideInput.recordingFailed}
        </div>
        <button type="button" onClick={onCancel} style={pill}>{tr.sideInput.close}</button>
      </div>
    )
  }

  return null
}

// ── Shared styles ──────────────────────────────────────────────────────────────

const pill: React.CSSProperties = {
  padding: '4px 10px',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 12, fontWeight: 500, cursor: 'pointer',
  color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)',
}
